import {
  locationTemplate,
  locationController,
  travelExpenseTypeTemplate,
  travelExpenseTypeController
} from '../modals';

import moment from 'moment';
import { sortBy } from 'lodash';

class TravelExpenseController {

  constructor(CompanyService, toastr, $scope, $http, $rootScope, $stateParams, $state, $uibModal, TravelExpenseService) {
    'ngInject';

    this.moment = moment;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.toastr = toastr;
    this.state = $state;
    this.$stateParams = $stateParams;
    this.activeUsers = [];
    this.$uibModal = $uibModal;
    this.travelExpenseService = TravelExpenseService;
    this.step = 1;

    this.travelExpense = {
      expenses: [],
      status: 'DRAFT'
    };
    this.projects = [];
    this.loading = true;
    this.proxyImport = this.$stateParams.proxyImport === true
      || this.$stateParams.proxyImport === 'true';

    this.loadProjects().then(() => this.loadExistingReportIfNeeded());
  }

  loadProjectsForUser(userId) {
    return this.travelExpenseService.getExpenseProjectOptionsForUser(userId).then((projects) => {
      this.projects = projects;
      this.reconcileBillablePurpose();
    });
  }

  loadProjects() {
    return this.$http.get(this.$rootScope.apiHost + '/api/users/me', { withCredentials: true }).then((response) => {
      const projects = response.data.availableChargeCodes
        .filter((cc) => cc.billable && cc.taskId && cc.taskName)
        .map((cc) => ({
          taskId: cc.taskId,
          taskName: cc.taskName
        }));

      this.projects = sortBy(
        Array.from(new Set(projects.map((p) => p.taskId)))
          .map((taskId) => projects.find((p) => p.taskId === taskId)),
        'taskName'
      );

      this.reconcileBillablePurpose();
    });
  }

  loadExistingReportIfNeeded() {
    const expenseReportId = this.$stateParams.expenseReportId;

    if (!expenseReportId) {
      this.loading = false;
      return;
    }

    this.loading = true;
    this.travelExpenseService.getExpenseReport(expenseReportId).then((response) => {
      const report = this.travelExpenseService.normalizeExpenseReportDates(response.data);
      const isProxy = this.proxyImport || !!report.proxyCreatedByUserId;

      if (!isProxy && report.status && report.status !== 'DRAFT' && report.status !== 'REJECTED') {
        this.loading = false;
        this.toastr.warning('This expense report cannot be edited in the create wizard.', 'Unable to Open');
        this.state.go('travel-expense-details', { expenseReportId: report.id });
        return;
      }

      this.proxyImport = isProxy;
      this.travelExpense = report;
      this.travelExpense.expenses = report.expenses || [];
      this.travelExpenseService.hydrateEstimateBasisDisplay(this.travelExpense);
      this.travelExpenseService.hydrateMileageDisplay(this.travelExpense);
      this.step = this.travelExpenseService.resolveWizardStep(report);

      const projectLoad = isProxy && report.userId
        ? this.loadProjectsForUser(report.userId)
        : Promise.resolve();

      return projectLoad.then(() => {
        this.reconcileBillablePurpose();
        this.loading = false;
      });
    }, () => {
      this.loading = false;
      this.toastr.error('Could not load the expense report.', 'Error');
      this.state.go(this.proxyImport ? 'admin.expenses' : 'expenses');
    });
  }

  proxyBannerText() {
    const report = this.travelExpense || {};
    const employee = [report.firstName, report.lastName].filter(Boolean).join(' ') || 'employee';
    const manager = [report.proxyCreatedByFirstName, report.proxyCreatedByLastName].filter(Boolean).join(' ');
    if (manager) {
      return 'Entered by ' + manager + ' on behalf of ' + employee + '. Status emails are not sent.';
    }
    return 'Proxy import on behalf of ' + employee + '. Status emails are not sent.';
  }

  openReviewDetails() {
    if (!this.travelExpense || !this.travelExpense.id) {
      return;
    }
    this.state.go('expense-review-details', { expenseReportId: this.travelExpense.id });
  }

  openActuals() {
    if (!this.travelExpense || !this.travelExpense.id) {
      return;
    }
    const params = { expenseReportId: this.travelExpense.id };
    if (this.proxyImport) {
      params.proxyImport = 'true';
    }
    this.state.go('travel-expense-actuals', params);
  }

  reconcileBillablePurpose() {
    if (!this.hasBillableProjects() && this.travelExpense.purpose === 'customer') {
      this.travelExpense.purpose = '';
      this.travelExpense.project = '';
    }
  }

  hasBillableProjects() {
    return this.projects && this.projects.length > 0;
  }

  isBillableTrip() {
    return this.travelExpense.purpose === 'customer';
  }

  getItineraryLocations() {
    return (this.travelExpense.expenses || []).filter((expense) => expense.itinerary);
  }

  getAdditionalExpenses() {
    return (this.travelExpense.expenses || []).filter((expense) => !expense.itinerary);
  }

  getItineraryColumnCount(includeActions) {
    let count = 4; // location, arrival, departure, days
    if (this.isBillableTrip()) {
      count += 5; // meals rate, total meals, lodging rate, total lodging, per diem
    }
    if (includeActions) {
      count += 1;
    }
    return count;
  }

  onPurposeChange() {
    if (this.travelExpense.purpose === 'customer' && !this.hasBillableProjects()) {
      this.travelExpense.purpose = '';
      this.travelExpense.project = '';
      this.toastr.warning('Billable customer travel requires an assigned customer project.', 'No Projects Available');
      return;
    }

    if (this.travelExpense.purpose !== 'customer') {
      this.travelExpense.project = '';
    }
  }

  removeExpense(report, index) {
    report.splice(index, 1);
  }

  removeLocation(location) {
    const expenses = this.travelExpense.expenses || [];
    const index = expenses.indexOf(location);
    if (index !== -1) {
      expenses.splice(index, 1);
    }
  }

  addExpense() {
    this.openExpenseModal(null);
  }

  editExpense(expense) {
    this.openExpenseModal(expense);
  }

  openExpenseModal(expenseToEdit) {
    const self = this;
    const modalInstance = this.$uibModal.open({
      template: travelExpenseTypeTemplate,
      controller: travelExpenseTypeController,
      controllerAs: '$ctrl',
      resolve: {
        items() {
          return [];
        },
        existingExpense() {
          return expenseToEdit || null;
        }
      },
      windowClass: 'hmodal-success'
    });

    modalInstance.addExpenseToReport = function(expense) {
      self.travelExpense.expenses.push(expense);
    };

    modalInstance.updateExpenseInReport = function(original, updated) {
      self.applyExpenseEdits(original, updated);
    };
  }

  applyExpenseEdits(original, updated) {
    const expenses = this.travelExpense.expenses || [];
    const index = expenses.indexOf(original);

    if (index === -1) {
      return;
    }

    const target = expenses[index];

    target.type = updated.type;
    target.description = updated.description;
    target.amount = updated.amount;
    target.mileage = updated.mileage;
    target.estimateBasisFile = updated.estimateBasisFile;
    target.estimateBasisFileName = updated.estimateBasisFileName;

    // New file replaces prior evidence; remember receipt ids to delete on save, and clear
    // local receipts so the UI shows the pending file name.
    if (updated.estimateBasisFile) {
      target.pendingReceiptIdsToDelete = (target.receipts || [])
        .map((receipt) => receipt.receipt || receipt.id)
        .filter(Boolean);
      target.receipts = [];
    }
  }

  addLocation() {
    const self = this;
    const modalInstance = this.$uibModal.open({
      template: locationTemplate,
      controller: locationController,
      controllerAs: '$ctrl',
      resolve: {
        billableTrip() {
          return self.isBillableTrip();
        },
        existingLocation() {
          return null;
        }
      },
      windowClass: 'hmodal-success'
    });

    modalInstance.addLocationToItinerary = function(location) {
      self.travelExpense.expenses.push(location);
    };
  }

  cancel() {
    this.state.go('expenses');
  }

  goToStep(step) {
    if (step < this.step) {
      this.step = step;
      return;
    }

    if (step === 2 && !this.canProceedFromBasicInfo()) {
      return;
    }

    if (step === 3 && !this.canProceedFromItinerary()) {
      return;
    }

    if (step === 4 && !this.canProceedFromItinerary()) {
      return;
    }

    this.step = step;
  }

  continueToItinerary() {
    if (!this.canProceedFromBasicInfo()) {
      this.toastr.warning('Complete all required basic information fields.', 'Incomplete');
      return;
    }

    this.step = 2;
  }

  continueToAdditionalExpenses() {
    if (!this.canProceedFromItinerary()) {
      this.toastr.warning('Add at least one itinerary location before continuing.', 'Incomplete');
      return;
    }

    this.step = 3;
  }

  continueToSummary() {
    if (!this.canProceedFromItinerary()) {
      this.toastr.warning('Add at least one itinerary location before continuing.', 'Incomplete');
      return;
    }

    this.step = 4;
  }

  getPurposeLabel() {
    if (this.travelExpense.purpose === 'customer') {
      return 'In Support of Customer (Billable)';
    }

    if (this.travelExpense.purpose === 'other') {
      return 'Other Business Travel (Non-Billable)';
    }

    return '';
  }

  getProjectName() {
    if (!this.travelExpense.project) {
      return '';
    }

    const match = (this.projects || []).find((project) => String(project.taskId) === String(this.travelExpense.project));
    return match ? match.taskName : this.travelExpense.project;
  }

  canProceedFromBasicInfo() {
    const expense = this.travelExpense;

    if (!expense.name || !expense.name.trim()) {
      return false;
    }

    if (!expense.purpose) {
      return false;
    }

    if (!expense.description || !expense.description.trim()) {
      return false;
    }

    if (expense.purpose === 'customer') {
      if (!this.hasBillableProjects() || !expense.project) {
        return false;
      }
    }

    return true;
  }

  canProceedFromItinerary() {
    return this.travelExpenseService.hasItinerary(this.travelExpense);
  }

  getAdditionalExpensesTotal() {
    return this.travelExpense.expenses
      .filter((expense) => !expense.itinerary)
      .reduce((total, expense) => total + (expense.amount || 0), 0);
  }

  canSaveDraft() {
    return !!(this.travelExpense.name && this.travelExpense.name.trim());
  }

  canSubmitExpenseReport() {
    return this.canProceedFromBasicInfo()
      && this.canProceedFromItinerary()
      && this.travelExpenseService.getEstimatedTotalForExpenseReport(this.travelExpense) > 0;
  }

  saveDraft() {
    if (!this.canSaveDraft()) {
      this.toastr.warning('Enter a Travel Report Name before saving a draft.', 'Incomplete');
      return;
    }

    this.loading = true;
    this.travelExpense.status = 'DRAFT';
    this.travelExpense.wizardStep = this.step;

    this.travelExpenseService.saveDraftExpenseReport(this.travelExpense).then((response) => {
      const savedReport = response.data;

      return this.travelExpenseService.uploadPendingEstimateBasisFiles(this.travelExpense, savedReport).then((refreshedReport) => {
        this.applySavedReport(refreshedReport || savedReport);
        this.loading = false;
        this.toastr.success('Draft expense report saved. You can reopen it anytime to continue.', 'Draft Saved');
      }, () => {
        this.applySavedReport(savedReport);
        this.loading = false;
        this.toastr.warning('Draft saved, but one or more estimate basis documents could not be uploaded.', 'Upload Warning');
      });
    }, (error) => {
      this.loading = false;
      this.toastr.error(this.getApiErrorMessage(error, 'Could not save the draft expense report.'), 'Error');
    });
  }

  applySavedReport(savedReport) {
    const normalized = this.travelExpenseService.normalizeExpenseReportDates(savedReport);
    this.travelExpenseService.hydrateEstimateBasisDisplay(normalized);
    this.travelExpenseService.hydrateMileageDisplay(normalized);
    const previousStep = this.step;

    this.travelExpense = normalized;
    this.travelExpense.expenses = normalized.expenses || [];
    this.step = previousStep || this.travelExpenseService.resolveWizardStep(normalized);

    if (this.travelExpense.id && String(this.$stateParams.expenseReportId) !== String(this.travelExpense.id)) {
      this.state.go('travel-expense', { expenseReportId: this.travelExpense.id }, { location: 'replace', notify: false });
    }
  }

  createExpenseReport() {
    if (!this.canSubmitExpenseReport()) {
      this.toastr.warning('Complete basic information, itinerary, and estimated amounts before submitting.', 'Incomplete');
      return;
    }

    this.loading = true;
    this.travelExpense.status = 'SUBMITTED';
    this.travelExpense.wizardStep = 4;

    this.travelExpenseService.submitExpenseReportForApproval(this.travelExpense).then((response) => {
      const createdReport = response.data;

      return this.travelExpenseService.uploadPendingEstimateBasisFiles(this.travelExpense, createdReport).then(() => {
        this.loading = false;
        this.toastr.success('Expense report submitted for approval.', 'Submitted');
        this.state.go('expenses');
      }, () => {
        this.loading = false;
        this.toastr.warning('The expense report was submitted, but one or more estimate basis documents could not be uploaded.', 'Upload Warning');
        this.state.go('expenses');
      });
    }, (error) => {
      this.loading = false;
      this.toastr.error(this.getApiErrorMessage(error, 'Could not submit the expense report.'), 'Error');
    });
  }

  getApiErrorMessage(error, fallback) {
    const data = error && error.data;
    if (!data) {
      return fallback;
    }

    if (typeof data === 'string' && data.trim()) {
      return data;
    }

    if (data.message) {
      return data.message;
    }

    if (data.error && typeof data.error === 'string') {
      return data.error;
    }

    return fallback;
  }
}

export default TravelExpenseController;



// WEBPACK FOOTER //
// ./client/pages/expenses/travel-expense/travel-expense.controller.js